import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { Browser, Cdn } from '@core/services';
import { NgbCarousel, NgbCarouselConfig, NgbCarouselModule } from '@ng-bootstrap/ng-bootstrap';
import { Gallery, ImageItem } from 'ng-gallery';
import { Lightbox, LightboxModule } from 'ng-gallery/lightbox';
import { NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap';
// import 'pannellum/build/pannellum.js';
import { TranslateModule } from '@ngx-translate/core';
import { Rating } from '../rating/rating.component';
import { Valoration } from '../valoration/valoration.component';
import { YoutubeVideo } from '../youtube-video/youtube-video.component';

import { CdnUrlPipe } from '@shared/pipes';
import { LazyLoadImageModule } from 'ng-lazyload-image';

/** variable que sera utilizada para renderizar las imagenes 360 */
declare var pannellum: any;

/**
 * ### **CarouselComponent**
 *
 * **Selector:** ``<app-carousel>``
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el Componente
 * encargado de renderizar el carousel de fotos o la galeria que le llega.
 */
@Component({
  selector: 'app-carousel',
  imports: [
    TranslateModule,
    Rating,
    Valoration,
    NgbCarouselModule,
    LightboxModule,
    YoutubeVideo,
    CdnUrlPipe,
    LazyLoadImageModule,
  ],
  templateUrl: "./carousel.component.html",
  styleUrl: "./carousel.component.scss",
  providers: [NgbCarouselConfig],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Carousel implements OnChanges {
  protected readonly config = inject(NgbCarouselConfig);
  private readonly gallery = inject(Gallery);
  private readonly cdn = inject(Cdn);
  public readonly lightbox = inject(Lightbox);
  private readonly browser = inject(Browser);

  /** HostBindinig: permite establecer propiedades en el elemento o componente, en este caso definir clases Css sobre el mismo*/
  @HostBinding('class.col-md-12') col12 = true;
  /** Indica si se muestra el botón de reservar */
  readonly showButtonReservation = input<boolean>(false);
  /** Variable que guardará la calificación que le llega desde el componente padre*/
  readonly calification = input<boolean>(true);
  /** Variable que guardará el estado que respresenta si un geomarcador o no que le envia el componente padre*/
  readonly marker = input<boolean>(false);
  /** Indica si se ocultan elementos del caourlse */
  readonly room = input<boolean>(false);
  /** Variable que guardará el path del logo que le envia el componente padre*/
  readonly logo_geomarker = input<string>('');
  /** Variable que guardará el nombre que le envia el componente padre.*/
  readonly name = input<string>('');
  /** Variable que guardara la lista de imagenes que le envia el componente padre*/
  readonly images = input<
    {
      image: string;
      is_360: boolean;
      title: string;
      video?: string;
    }[]
  >();
  /** Variable que guardara la cantidad de valoraciones, este dato llega desde un componente padre*/
  readonly valorations = input<number>(0);
  /** Variable que guardara la calificación que le envia el componente padre*/
  readonly qualification = input<number>(0);
  /** Variable que guardara la galeria que tendra la caracteristica lightbox*/
  readonly galleryLightbox = input<boolean>(false);
  /** Variable que guardara y emitira el index de la imagen que sea seleccionada */
  readonly imageIndexSelected = output<number>();
  /** Variable que redirigira al usuario directamente al formulario de reserva  */
  readonly goToBooking = output<void>();
  /** permite obtener las instancias de elementos nativos, directivas y componentes que estén en el template del mismo.*/
  private readonly carousel = viewChild(NgbCarousel);
  /** Variable que guardara el index seleccionado*/
  protected readonly indexSelected = signal<number>(0);

  /**@ignore */
  constructor() {
    if (this.browser.isBrowser) {
      // @ts-ignore
      import('pannellum/build/pannellum.js')
        .then((modulo) => {
          // Usa el módulo aquí
          console.log('Módulo pannellum cargado:', modulo);
        })
        .catch((error) => {
          // Maneja el error aquí
          console.error('Error al cargar el módulo pannellum:', error);
        });
    }

    // customize default values of carousels used by this component tree
    this.config.showNavigationArrows = true;
    this.config.showNavigationIndicators = true;
  }

  /**
   * Función nativa de angular que detecta los cambios sobre las variables inputs del sistema,
   * en este caso cada haya un cambio sobre his.galleryLightbox && this.images llamara al metodo this.setLightBox()
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.galleryLightbox || changes.images) {
      if (this.galleryLightbox() && this.images()) {
        this.setLightBox();
      }
    }
  }

  /**
   * función nativa de angular que se activa despues del ngOnInit que llama al método setVrImages().
   */
  ngAfterViewInit(): void {
    setTimeout(() => this.setVrImages(), 100);
  }

  /**
   *  Funcion encargada de obtener el indice de la imagen que se esta viendo actualmente en el carousel y emitir el mismo.
   * @param event Parametro que me trae el indice actual.
   */
  protected getIndex(event: NgbSlideEvent): void {
    const indexSelected = +event.current.split('-')[event.current.split('-').length - 1];
    this.indexSelected.set(indexSelected);
    this.imageIndexSelected.emit(indexSelected);
  }

  /**
   * Función que redimenciona la imagen una vez ha sido seleccionada, para esto hace uso del evento resize.
   * @param index
   */
  protected selectImage(index: number): void {
    this.carousel()?.select(index.toString());
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }

  /**
   * función encargada de cargar las imagenes al lightbox para que cuando el usuario ahga zoom
   * o clic en la imagen la pueda ver como una galeria.
   */
  private setLightBox(): void {
    const images = this.images();
    if (!images) return;

    const lightboxRef = this.gallery.ref('lightbox');
    // Load items into the lightbox gallery ref}
    lightboxRef.load(
      images
        .filter((i) => !i.is_360)
        .map(
          (i) =>
            new ImageItem({
              src: this.cdn.replaceUrlBucketWithWidth(i.image, 1400),
              thumb: this.cdn.replaceUrlBucketWithWidth(i.image, 500),
            }),
        ),
    );
  }

  /**
   * función encargada de redimencionar las imagenes para los elementos(imagenes) 360° haciendo uso del evento resize.
   */
  private setVrImages(): void {
    const images = this.images();
    if (!images || !this.browser.isBrowser) return;

    images.forEach((image, i) => {
      if (image.is_360) {
        const carouselElement = document.getElementById(`carousel-image-${i}`);
        pannellum
          .viewer(carouselElement, {
            type: 'equirectangular',
            panorama: image.image,
            autoLoad: true,
            autoRotate: -2,
          })
          .on('load', () => {
            window.dispatchEvent(new Event('resize'));
          });
      }
    });
  }

  /**
   * Función que redirige al usuario al formulario de reserva.
   */
  protected goToBookingForm(): void {
    this.goToBooking.emit();
  }
}
